import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    GUIDE_TITLE,
    GUIDE_SUBTITLE,
    GUIDE_BYLINE,
    GUIDE_PREFACE,
    GUIDE_SHIFTS,
    GUIDE_CLOSING,
} from "@/lib/guide";
import { PORTRAIT_URL, MOOD_IMAGES, HERO_IMAGE, LEAD_MAGNET_IMAGE } from "@/lib/content";

/**
 * The Considered Home — designed editorial booklet.
 * One scrollable document; each .guide-page is a single A4-ish spread.
 * "Save as PDF" triggers window.print() (print CSS handles page breaks).
 */

const SHIFT_IMAGES = [
    HERO_IMAGE,                 // 01 Light
    LEAD_MAGNET_IMAGE,          // 02 One thread (linen)
    MOOD_IMAGES[5]?.url,        // 03 Subtract
    MOOD_IMAGES[0]?.url,        // 04 Anchor (chocolate velvet)
    MOOD_IMAGES[6]?.url,        // 05 Texture
];

const COVER_IMAGE = MOOD_IMAGES[1]?.url;

function PageBreak() {
    return <div className="guide-pagebreak" aria-hidden="true" />;
}

export default function Guide() {
    useEffect(() => {
        document.title = "The Sacred Home — Arc & Light";
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (
        <div data-testid="guide-page" className="guide-root bg-pearl text-cacao min-h-screen">
            {/* Floating actions — hidden in print */}
            <div className="guide-actions print:hidden fixed top-6 right-6 md:top-8 md:right-10 z-50 flex items-center gap-3 md:gap-5">
                <Link
                    to="/"
                    data-testid="guide-back"
                    className="text-cacao/70 hover:text-clay transition-colors duration-500 text-[0.72rem] tracking-[0.24em] uppercase bg-pearl/85 backdrop-blur-md px-4 py-2 border border-cacao/10"
                >
                    ← Home
                </Link>
                <button
                    type="button"
                    data-testid="guide-print"
                    onClick={() => window.print()}
                    className="btn-clay !py-2 !px-4 !text-[0.66rem]"
                >
                    Save as PDF
                </button>
            </div>

            {/* ─────────── COVER ─────────── */}
            <section className="guide-page guide-cover" data-testid="guide-cover">
                <div className="guide-cover-inner">
                    <div className="guide-cover-image">
                        <img src={COVER_IMAGE} alt="Folded raw linen — quiet, tactile, considered" />
                    </div>
                    <div className="guide-cover-text">
                        <p className="eyebrow text-clay">
                            <span className="divider-line text-clay/60 mr-3 align-middle" />
                            A guide from Arc &amp; Light
                        </p>
                        <h1 className="font-serif text-cacao guide-cover-title">
                            {GUIDE_TITLE}
                        </h1>
                        <p className="font-serif italic text-clay-deep guide-cover-sub">
                            {GUIDE_SUBTITLE}
                        </p>
                        <div className="guide-cover-foot">
                            <p className="font-script text-clay-deep text-[3.2rem] leading-none">
                                Merilyn
                            </p>
                            <p className="eyebrow text-cacao/55 mt-2">{GUIDE_BYLINE}</p>
                        </div>
                    </div>
                </div>
            </section>

            <PageBreak />

            {/* ─────────── PREFACE ─────────── */}
            <section className="guide-page" data-testid="guide-preface">
                <div className="guide-spread">
                    <div className="guide-spread-side">
                        <p className="eyebrow text-cacao/55 mb-6">
                            <span className="divider-line text-cacao/35 mr-3 align-middle" />
                            {GUIDE_PREFACE.eyebrow}
                        </p>
                        <h2 className="font-serif text-cacao text-[2.4rem] md:text-[3.2rem] leading-[1.05]">
                            {GUIDE_PREFACE.title}
                        </h2>
                    </div>
                    <div className="guide-spread-side guide-prose">
                        {GUIDE_PREFACE.body.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                        <p className="guide-signature">
                            <span className="font-script text-clay-deep text-[2.6rem] leading-none block">
                                {GUIDE_PREFACE.signature}
                            </span>
                            <span className="eyebrow text-cacao/55 mt-1 block">
                                Merilyn Smith
                            </span>
                        </p>
                    </div>
                </div>
            </section>

            <PageBreak />

            {/* ─────────── FIVE SHIFTS ─────────── */}
            {GUIDE_SHIFTS.map((shift, i) => (
                <div key={shift.n}>
                    <section
                        className="guide-page"
                        data-testid={`guide-shift-${shift.n}`}
                    >
                        <div className="guide-shift">
                            <div className="guide-shift-image">
                                {SHIFT_IMAGES[i] && (
                                    <img src={SHIFT_IMAGES[i]} alt={`Quiet study — ${shift.title}`} />
                                )}
                            </div>
                            <div className="guide-shift-text">
                                <div className="guide-shift-num">
                                    <span className="font-serif text-cacao/22 text-[8rem] md:text-[10rem] leading-none">
                                        {shift.n}
                                    </span>
                                    <span className="eyebrow text-clay">
                                        <span className="divider-line text-clay/60 mr-3 align-middle" />
                                        Shift {shift.n}
                                    </span>
                                </div>
                                <h2 className="font-serif text-cacao text-[2rem] md:text-[2.8rem] leading-[1.06] mt-4 max-w-[22ch]">
                                    {shift.title}
                                </h2>
                                <p className="font-serif italic text-clay-deep text-[1.2rem] md:text-[1.4rem] leading-[1.5] mt-6 max-w-[34rem]">
                                    {shift.lede}
                                </p>
                                <div className="guide-prose mt-8">
                                    {shift.body.map((p, j) => (
                                        <p key={j}>{p}</p>
                                    ))}
                                </div>
                                <aside className="guide-try" data-testid={`guide-try-${shift.n}`}>
                                    <p className="eyebrow text-clay mb-3">Try this</p>
                                    <p className="font-serif italic text-cacao text-[1.05rem] md:text-[1.15rem] leading-[1.55]">
                                        {shift.tryThis}
                                    </p>
                                </aside>
                            </div>
                        </div>
                    </section>
                    <PageBreak />
                </div>
            ))}

            {/* ─────────── CLOSING ─────────── */}
            <section className="guide-page" data-testid="guide-closing">
                <div className="guide-closing">
                    <p className="eyebrow text-clay mb-8">
                        <span className="divider-line text-clay/60 mr-3 align-middle" />
                        In closing
                    </p>
                    <h2 className="font-serif text-cacao text-[2.6rem] md:text-[3.6rem] leading-[1.05] max-w-[22ch]">
                        {GUIDE_CLOSING.title}
                    </h2>
                    <div className="guide-prose mt-10 max-w-[36rem]">
                        {GUIDE_CLOSING.body.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>

                    <div className="guide-closing-sign mt-14">
                        <p className="font-script text-clay-deep text-[3rem] leading-none">
                            {GUIDE_CLOSING.sign}
                        </p>
                        <p className="eyebrow text-cacao/55 mt-2">{GUIDE_CLOSING.role}</p>
                    </div>

                    <div className="guide-closing-cta print:hidden">
                        <Link
                            to="/#offer"
                            data-testid="guide-cta-book"
                            className="btn-clay"
                        >
                            Book a Discovery Consultation
                        </Link>
                        <Link
                            to="/"
                            className="btn-ghost"
                        >
                            Return to Arc &amp; Light
                        </Link>
                    </div>
                </div>
            </section>

            {/* ─────────── COLOPHON ─────────── */}
            <section className="guide-page guide-colophon" data-testid="guide-colophon">
                <div className="guide-colophon-inner">
                    <div className="guide-colophon-portrait">
                        <img src={PORTRAIT_URL} alt="Merilyn Smith" />
                    </div>
                    <div className="guide-colophon-text">
                        <p className="font-serif text-cacao text-[1.4rem] md:text-[1.8rem] leading-[1.3] max-w-[28rem]">
                            <em className="italic">When a space feels aligned, life flows more beautifully within it.</em>
                        </p>
                        <p className="font-script text-clay-deep text-[2.4rem] leading-none mt-6">
                            Merilyn Smith
                        </p>

                        <div className="guide-colophon-meta">
                            <p className="eyebrow text-cacao/55">Arc &amp; Light</p>
                            <p className="text-cacao/75 text-[0.95rem] leading-[1.7] mt-1">
                                Interior Design Consultancy · Brisbane
                            </p>
                            <p className="text-cacao/55 text-[0.72rem] tracking-[0.24em] uppercase mt-3">
                                Norman Park · Bulimba · Hawthorne · Camp Hill · and surrounding suburbs
                            </p>
                            <p className="font-serif italic text-cacao/85 mt-6 text-[1.05rem]">
                                merilyn@merilyn.com.au
                            </p>
                        </div>

                        <p className="guide-colophon-c">
                            © {new Date().getFullYear()} Arc &amp; Light. All rights reserved.
                            This guide is shared for personal use.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
